import React, { useState, useRef, useEffect } from "react";
import { Button, Form, message } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import logo from "../../../Assets/Images/logo.svg";
import logoCompany from "../../../Assets/Icons/companylogo.svg";
import InputCard from "../../../Components/Cards/InputCard";
import FuelType from "../../../Components/Cards/FuelTypeCard";
import GenericOperatingHours from "../../../Components/Tables/GenericOperatingHourTables";
import GenericDocumentManagerTable from "../../../Components/Tables/GenericDocumentManagerTable";
import LocationMap from "../../../Components/Map/LocationPicker";
import "./style.css";
import { submitOnboardingRequest } from "../../../API/FuelVendor/SignUp";
import {
  getFuelTypes,
  getVendorTypes,
  getFuelBrands,
  getDocumentCategories,
} from "../../../API/Config";

const FuelVendorSignUp = ({ setUser }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchedLatitude = Form.useWatch("latitude", form);
  const watchedLongitude = Form.useWatch("longitude", form);
  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [selectedFuels, setSelectedFuels] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [operatingHours, setOperatingHours] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [availableFuelTypes, setAvailableFuelTypes] = useState([]);
  const [vendorTypeOptions, setVendorTypeOptions] = useState([]);
  const [fuelBrandOptions, setFuelBrandOptions] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchFuelTypes = async () => {
      const res = await getFuelTypes();
      const types = res?.data?.data || res?.data?.results || res?.data;

      if (Array.isArray(types)) {
        setAvailableFuelTypes(types);
      }
    };

    fetchFuelTypes();
  }, []);

  useEffect(() => {
    const fetchVendorTypes = async () => {
      try {
        const response = await getVendorTypes();
        const list = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || response?.data?.results || [];

        if (Math.floor(response?.status / 100) === 2 && Array.isArray(list)) {
          setVendorTypeOptions(
            list.map((type) =>
              typeof type === "string"
                ? type
                : { label: type.name, value: type.name },
            ),
          );
        }
      } catch (err) {
        console.error("Failed to load vendor types:", err);
      }
    };

    fetchVendorTypes();
  }, []);

  useEffect(() => {
    const fetchFuelBrands = async () => {
      try {
        const response = await getFuelBrands();
        const list = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data || response?.data?.results || [];

        if (Math.floor(response?.status / 100) === 2 && Array.isArray(list)) {
          setFuelBrandOptions(
            list.map((brand) =>
              typeof brand === "string"
                ? { label: brand, value: brand }
                : { label: brand.name, value: brand.id ?? brand.name },
            ),
          );
        }
      } catch (err) {
        console.error("Failed to load fuel brands:", err);
      }
    };

    fetchFuelBrands();
  }, []);

  useEffect(() => {
    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fuel-vendor");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchDocumentCategories();
  }, []);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedFuels.length) {
        message.error("Please select at least one fuel type.");
        return;
      }
      if (!documents.length) {
        message.error("Please upload at least one document.");
        return;
      }

      const formData = new FormData();
      formData.append("legal_name", values.legalEntityName);
      formData.append("brand_name", values.brandName);
      formData.append("tax_id", values.taxId);
      formData.append(
        "date_incorporated",
        values.dateIncorporated.format("YYYY-MM-DD"),
      );
      formData.append("vendor_type", values.vendorType);
      formData.append("contact_phone", values.phoneNumber);
      formData.append("address", values.hqAddress);
      formData.append("primary_contact_person", values.primaryContact);
      formData.append("contact_email", values.businessEmail);
      formData.append("registration_contact_phone", values.companyContactNumber);
      formData.append("bank_name", values.bankName);
      formData.append("bank_address", values.bankAddress);
      formData.append("account_holder_name", values.accountHolderName);
      formData.append("account_number", values.accountNumber);
      formData.append("account_type", values.accountType);
      formData.append("swift_code", values.swiftCode);
      formData.append("currency", values.currency);
      formData.append("wipay_account_number", values.wipayAccountNumber || "");
      formData.append("wipay_api_key", values.wipayApiKey || "");
      formData.append("station_name", values.stationName);
      formData.append("station_id", values.stationId);
      formData.append("station_address", values.stationAddress);
      formData.append("fuel_brand_id", values.fuelBrand || "");
      formData.append("latitude", values.latitude);
      formData.append("longitude", values.longitude);
      formData.append("password", values.password);
      formData.append("confirm_password", values.confirmPassword);
      if (avatarFile) {
        formData.append("avatar", avatarFile);
      }

      const fuelTypeIdByName = {};
      availableFuelTypes.forEach((f) => {
        fuelTypeIdByName[f.name] = f.id;
      });
      selectedFuels
        .map((name) => fuelTypeIdByName[name])
        .filter((id) => id !== undefined)
        .forEach((id) => {
          formData.append("fuel_types_offered", id);
        });

      formData.append("operating_hours", JSON.stringify(operatingHours));
      documents.forEach((doc) => {
        formData.append("documents", doc.file);
      });
      formData.append(
        "document_categories",
        JSON.stringify(documents.map((doc) => doc.category)),
      );

      setSubmitting(true);
      const res = await submitOnboardingRequest(formData);
      setSubmitting(false);

      if (res?.status === 200 || res?.status === 201) {
        navigate("/request-submitted-vendor");
      } else {
        const data = res?.data;
        if (typeof data?.detail === "string") {
          message.error(data.detail);
        } else if (data) {
          const messages = Object.entries(data).flatMap(([field, errors]) => {
            const list = Array.isArray(errors) ? errors : [errors];
            return list.map((err) => `${field.replace(/_/g, " ")}: ${err}`);
          });
          message.error(
            messages.length
              ? messages.join(" | ")
              : "Failed to submit onboarding request.",
          );
        } else {
          message.error("Failed to submit onboarding request.");
        }
      }
    } catch {
      message.error("Please fill in all required fields.");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAvatar(null);
    setAvatarFile(null);
    setSelectedFuels([]);
    setDocuments([]);
    setOperatingHours([]);
  };

  const registrationFields = [
    {
      name: "legalEntityName",
      label: "Legal Entity Name",
      placeholder: "Enter Legal Entity Name",
      type: "text",
      fullWidth: true,
      rules: [{ required: true, message: "Legal entity name is required" }],
    },
    {
      name: "brandName",
      label: "Brand Name",
      placeholder: "Enter Brand Name",
      type: "text",
      rules: [{ required: true, message: "Brand name is required" }],
    },
    {
      name: "taxId",
      label: "Tax ID / EIN Number",
      placeholder: "e.g. 74-1234567",
      type: "text",
      rules: [{ required: true, message: "Tax ID is required" }],
    },
    {
      name: "dateIncorporated",
      label: "Date Incorporated",
      placeholder: "Select Date Incorporated",
      type: "date",
      rules: [{ required: true, message: "Date incorporated is required" }],
    },
    {
      name: "vendorType",
      label: "Vendor Type",
      placeholder: "Select Vendor Type",
      type: "select",
      options: vendorTypeOptions,
      rules: [{ required: true, message: "Vendor type is required" }],
    },
    {
      name: "companyContactNumber",
      label: "Company Contact Number",
      placeholder: "+1 (000) 000-0000",
      type: "phone",
      rules: [{ required: true, message: "Contact number is required" }],
    },
  ];

  const contactFields = [
    {
      name: "hqAddress",
      label: "Headquarters Address",
      placeholder: "Enter full headquarters address",
      type: "textarea",
      fullWidth: true,
      rules: [{ required: true, message: "Headquarters address is required" }],
    },
    {
      name: "primaryContact",
      label: "Primary Contact Person",
      placeholder: "Full Name",
      type: "text",
      rules: [{ required: true, message: "Primary contact is required" }],
    },
    {
      name: "businessEmail",
      label: "Business Email",
      placeholder: "email@company.com",
      type: "text",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter a valid email" },
      ],
    },
    {
      name: "phoneNumber",
      label: "Phone Number",
      placeholder: "+1 (000) 000-0000",
      type: "phone",
      rules: [{ required: true, message: "Phone number is required" }],
    },
  ];

  const bankingFields = [
    {
      name: "bankName",
      label: "Bank Name",
      placeholder: "Enter Bank Name",
      type: "text",
      rules: [{ required: true, message: "Bank name is required" }],
    },
    {
      name: "bankAddress",
      label: "Bank Address",
      placeholder: "Enter Bank Address",
      type: "text",
      rules: [{ required: true, message: "Bank address is required" }],
    },
    {
      name: "accountHolderName",
      label: "Account Holder Name",
      placeholder: "Enter Account Holder Name",
      type: "text",
      rules: [{ required: true, message: "Account holder name is required" }],
    },
    {
      name: "accountNumber",
      label: "Account Number",
      placeholder: "Enter Account Number",
      type: "text",
      rules: [{ required: true, message: "Account number is required" }],
    },
    {
      name: "accountType",
      label: "Account Type",
      placeholder: "Select",
      type: "select",
      options: ["Savings", "Current", "Checking"],
      rules: [{ required: true, message: "Account type is required" }],
    },
    {
      name: "swiftCode",
      label: "SWIFT / BIC Code",
      placeholder: "e.g. SWIFT-ABC-0123",
      type: "text",
      rules: [{ required: true, message: "SWIFT/BIC code is required" }],
    },
    {
      name: "currency",
      label: "Currency",
      placeholder: "Select currency",
      type: "currency",
      rules: [{ required: true, message: "Currency is required" }],
    },
    {
      name: "wipayAccountNumber",
      label: "WiPay Account Number",
      placeholder: "Enter WiPay Account Number",
      type: "text",
    },
    {
      name: "wipayApiKey",
      label: "WiPay API Key",
      placeholder: "Enter WiPay API Key",
      type: "text",
    },
  ];

  const passwordFields = [
    {
      name: "password",
      label: "Password",
      placeholder: "Enter Password",
      type: "password",
      rules: [
        { required: true, message: "Password is required" },
        { min: 6, message: "Password must be at least 6 characters" },
      ],
    },
    {
      name: "confirmPassword",
      label: "Confirm Password",
      placeholder: "Re-enter Password",
      type: "password",
      dependencies: ["password"],
      rules: [
        { required: true, message: "Please confirm your password" },
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || getFieldValue("password") === value) {
              return Promise.resolve();
            }
            return Promise.reject("Passwords do not match");
          },
        }),
      ],
    },
  ];

  const stationFields = [
    {
      name: "stationName",
      label: "Station Name",
      placeholder: "Enter Station Name",
      type: "text",
      rules: [{ required: true, message: "Station name is required" }],
    },
    {
      name: "stationId",
      label: "Station ID",
      placeholder: "e.g. STN-2024-8810-X91",
      type: "text",
      rules: [{ required: true, message: "Station ID is required" }],
    },
    {
      name: "stationAddress",
      label: "Full Physical Address",
      placeholder: "e.g. 1 Business Parkway, Suite 100, Houston TX",
      type: "textarea",
      fullWidth: true,
      rules: [{ required: true, message: "Station address is required" }],
    },
    {
      name: "fuelBrand",
      label: "Fuel Brand",
      placeholder: "Select Fuel Brand",
      type: "select",
      options: fuelBrandOptions,
      rules: [{ required: true, message: "Fuel brand is required" }],
    },
    {
      name: "latitude",
      label: "Latitude",
      placeholder: "e.g. 40.712",
      type: "text",
      rules: [
        { required: true, message: "Latitude is required" },
        {
          pattern: /^-?([1-8]?\d(\.\d+)?|90(\.0+)?)$/,
          message: "Enter a valid latitude (-90 to 90)",
        },
      ],
    },
    {
      name: "longitude",
      label: "Longitude",
      placeholder: "e.g. -74.006",
      type: "text",
      rules: [
        { required: true, message: "Longitude is required" },
        {
          pattern: /^-?(180(\.0+)?|1[0-7]\d(\.\d+)?|\d{1,2}(\.\d+)?)$/,
          message: "Enter a valid longitude (-180 to 180)",
        },
      ],
    },
  ];

  return (
    <div className="fv-signup">
      <Button
        className="signup-back"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
      />
      <div className="fv-signup__logo">
        <img src={logo} alt="logo" />
      </div>
      <div className="fv-signup__page-title">Fuel Vendors Onboarding</div>

      <div className="fv-signup__card">
        <Form form={form} layout="vertical">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={handleButtonClick}
                >
                  Upload Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <div className="profile-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                    }}
                  />
                ) : (
                  "T"
                )}
              </div>
            </div>
            <div className="profile-body"></div>
          </div>
          <InputCard
            title="Fuel Vendor Registration"
            fields={registrationFields}
          />
          <InputCard title="Contact Information" fields={contactFields} />
          <InputCard title="Banking & Settlement" fields={bankingFields} />

          <InputCard
            title="Fuel Station Information"
            fields={stationFields.slice(0, 4)}
          />
          <div className="fv-signup__gps-banner">
            <InfoCircleOutlined className="fv-signup__gps-icon" />
            <span>
              <div className="heading-banner">GPS Coordinate Requirement:</div>
              <div className="banner-sub-heading">
                Every Geo-GPS coordinates are mandatory. These are used to
                automatically validate fuel transactions ensuring accurate
                location verification.
              </div>
            </span>
          </div>
          <InputCard title="" fields={stationFields.slice(4)}>
            <LocationMap
              latitude={watchedLatitude ? parseFloat(watchedLatitude) : null}
              longitude={watchedLongitude ? parseFloat(watchedLongitude) : null}
              label="Station Location"
              onLocationSelect={({ latitude, longitude }) =>
                form.setFieldsValue({ latitude, longitude })
              }
            />
          </InputCard>
          {/* Fuel Type */}
          <FuelType
            fuelList={availableFuelTypes.map((f) => f.name)}
            selectedFuel={selectedFuels}
            onSelectFuel={setSelectedFuels}
          />
          {/* Operating Hours */}
          <GenericOperatingHours
            value={operatingHours}
            onChange={setOperatingHours}
          />
          {/* Upload Documents */}
          <GenericDocumentManagerTable
            showUploadOnly={true}
            setDocuments={setDocuments}
            initialDocuments={documents}
            documentTypes={documentCategories}
          />
          <InputCard title="Password" fields={passwordFields} />
        </Form>

        <div className="btns-row">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            className="submit-btn"
            onClick={handleSubmit}
            loading={submitting}
          >
            Submit for Approval
          </Button>
        </div>

        <div className="fv-signup__powered">
          <img
            src={logoCompany}
            alt="CouSync"
            className="fv-signup__powered-icon"
          />
          <div className="fv-signup__powered-text">
            Powered by <span>CouSync</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelVendorSignUp;
